/**
 * Cursor pagination hooks for `{ items, next_cursor }` list endpoints.
 *
 * - useInfiniteList: "Load more" style. Items accumulate across pages.
 * - useCursorPages: next/previous style. One page on screen at a time.
 *
 * Both return a `pagination` object you can pass straight to <DataTable>.
 */
import { keepPreviousData, useInfiniteQuery, useQuery, type QueryKey } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import type { Page } from './types'

const EMPTY: never[] = []

export interface LoadMorePagination {
  mode: 'load-more'
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
}

export interface PagedPagination {
  mode: 'pages'
  pageIndex: number
  hasNext: boolean
  hasPrev: boolean
  isFetching: boolean
  /** The rows on screen are the previous page, kept while the requested one loads. */
  isPlaceholder: boolean
  next: () => void
  prev: () => void
}

export type TablePagination = LoadMorePagination | PagedPagination

export function useInfiniteList<T>(options: {
  queryKey: QueryKey
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>
  enabled?: boolean
  refetchInterval?: number | false
}) {
  const query = useInfiniteQuery({
    queryKey: options.queryKey,
    queryFn: ({ pageParam }) => options.fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
  })
  const items = useMemo<T[]>(() => query.data?.pages.flatMap((p) => p.items) ?? EMPTY, [query.data])
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query
  const loadMore = useCallback(() => {
    void fetchNextPage()
  }, [fetchNextPage])
  const pagination: LoadMorePagination = {
    mode: 'load-more',
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore,
  }
  return { ...query, items, pagination }
}

export function useCursorPages<T>(options: {
  queryKey: QueryKey
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>
  enabled?: boolean
  /** Poll the page on screen (for example a live message log). */
  refetchInterval?: number | false
}) {
  const keyHash = JSON.stringify(options.queryKey)
  // The cursor stack resets whenever the query key (filters) changes.
  const [state, setState] = useState<{ key: string; cursors: (string | undefined)[] }>({
    key: keyHash,
    cursors: [undefined],
  })
  // Adjust state during render (not in an effect) so a changed key never
  // renders with a stale stack. Recording the new key also means that going
  // back to an earlier key (clearing a filter) starts again at page 1.
  if (state.key !== keyHash) setState({ key: keyHash, cursors: [undefined] })
  const cursors = state.key === keyHash ? state.cursors : [undefined]
  const cursor = cursors[cursors.length - 1]
  const query = useQuery({
    queryKey: [...options.queryKey, { cursor: cursor ?? null }],
    queryFn: () => options.fetchPage(cursor),
    enabled: options.enabled,
    placeholderData: keepPreviousData,
    refetchInterval: options.refetchInterval,
  })
  // While the requested page loads, `query.data` is the previous page (or the
  // previous filter's page) kept as a placeholder. Its cursor is not the next
  // cursor for what we asked for, so paging forward waits for real data.
  const settled = !query.isPlaceholderData
  const nextCursor = settled ? (query.data?.next_cursor ?? undefined) : undefined
  const pagination: PagedPagination = {
    mode: 'pages',
    pageIndex: cursors.length - 1,
    hasNext: !!nextCursor,
    hasPrev: cursors.length > 1,
    isFetching: query.isFetching,
    isPlaceholder: !settled,
    next: () => {
      if (!nextCursor) return
      // Functional update: two clicks in one tick must not push the same cursor twice.
      setState((s) =>
        s.key === keyHash && s.cursors[s.cursors.length - 1] === cursor
          ? { key: keyHash, cursors: [...s.cursors, nextCursor] }
          : s,
      )
    },
    prev: () => {
      setState((s) =>
        s.key === keyHash && s.cursors.length > 1 && s.cursors[s.cursors.length - 1] === cursor
          ? { key: keyHash, cursors: s.cursors.slice(0, -1) }
          : s,
      )
    },
  }
  const items = query.data?.items ?? (EMPTY as T[])
  return { ...query, items, pagination }
}
